import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, Download, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import {
  parseCSV,
  validateDeals,
  downloadTemplate,
  downloadErrorReport,
  type ParsedResult,
} from "@/utils/dealImport";

interface BulkImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const BulkImportDialog = ({ open, onOpenChange }: BulkImportDialogProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationResult, setValidationResult] = useState<ParsedResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.xlsx'))) {
      handleFileSelect(droppedFile);
    } else {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV or Excel file",
        variant: "destructive",
      });
    }
  }, []);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setValidationResult(null);
    setProgress(0);
    
    try {
      setIsProcessing(true);
      
      // Parse CSV
      const data = await parseCSV(selectedFile);
      
      // Get all athletes for lookup
      const { data: athletes } = await supabase
        .from("athletes")
        .select("id, name, email");
      
      // Validate
      const result = validateDeals(data, athletes || []);
      setValidationResult(result);
      
      if (result.invalid.length === 0) {
        toast({
          title: "Validation successful",
          description: `All ${result.summary.valid} deals are ready to import`,
        });
      } else {
        toast({
          title: "Validation completed with errors",
          description: `${result.summary.valid} valid, ${result.summary.invalid} invalid`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error processing file:", error);
      toast({
        title: "Error processing file",
        description: "Please check your CSV format and try again",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!validationResult || validationResult.valid.length === 0) return;
    
    try {
      setIsProcessing(true);
      setProgress(0);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      const deals = validationResult.valid.map(v => ({
        athlete_id: v.data.athlete_id!,
        title: v.data.title,
        description: v.data.description || null,
        brand_name: v.data.brand_name,
        brand_contact_email: v.data.brand_contact_email || null,
        brand_contact_phone: v.data.brand_contact_phone || null,
        deal_type: v.data.deal_type,
        amount: v.data.amount,
        start_date: v.data.start_date,
        end_date: v.data.end_date || null,
        deliverables: v.data.deliverables || null,
        category: v.data.category || null,
        status: 'pending_compliance' as const,
        import_source: 'csv_bulk' as const,
        submitted_by: user?.id,
      }));
      
      // Batch insert in chunks of 50
      const chunkSize = 50;
      let imported = 0;
      
      for (let i = 0; i < deals.length; i += chunkSize) {
        const chunk = deals.slice(i, i + chunkSize);
        const { error } = await supabase
          .from("deals")
          .insert(chunk);
        
        if (error) throw error;
        
        imported += chunk.length;
        setProgress((imported / deals.length) * 100);
      }
      
      // Create audit log
      await supabase.from("audit_logs").insert({
        action: "bulk_import_deals",
        entity_type: "deals",
        severity: "info",
        details: `Bulk imported ${imported} deals from CSV`,
        metadata: {
          file_name: file?.name,
          total_rows: validationResult.summary.total,
          successful: imported,
          failed: validationResult.summary.invalid,
        },
      });
      
      toast({
        title: "Import successful",
        description: `Successfully imported ${imported} deals`,
      });
      
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["pending-deals"] });
      onOpenChange(false);
      
      // Reset state
      setFile(null);
      setValidationResult(null);
      setProgress(0);
    } catch (error) {
      console.error("Error importing deals:", error);
      toast({
        title: "Import failed",
        description: "An error occurred during import. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Deals</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import multiple deals at once
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template Download */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium">Need a template?</p>
              <p className="text-sm text-muted-foreground">
                Download our CSV template to get started
              </p>
            </div>
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="mr-2 h-4 w-4" />
              Download Template
            </Button>
          </div>

          {/* File Upload */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25"
            }`}
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">
              {file ? file.name : "Drop your CSV file here"}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse
            </p>
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) handleFileSelect(selectedFile);
              }}
              className="hidden"
              id="csv-upload"
            />
            <label htmlFor="csv-upload">
              <Button variant="secondary" asChild>
                <span>Choose File</span>
              </Button>
            </label>
          </div>

          {/* Validation Results */}
          {validationResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Alert>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription>
                    <div className="font-medium">Valid</div>
                    <div className="text-2xl">{validationResult.summary.valid}</div>
                  </AlertDescription>
                </Alert>
                
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium">Invalid</div>
                    <div className="text-2xl">{validationResult.summary.invalid}</div>
                  </AlertDescription>
                </Alert>
                
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium">Total</div>
                    <div className="text-2xl">{validationResult.summary.total}</div>
                  </AlertDescription>
                </Alert>
              </div>

              {validationResult.invalid.length > 0 && (
                <Alert variant="destructive">
                  <AlertDescription className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Validation errors found</p>
                      <p className="text-sm">Download the error report to see details</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadErrorReport(validationResult.invalid)}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Error Report
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Preview of valid deals */}
              {validationResult.valid.length > 0 && (
                <div className="border rounded-lg p-4">
                  <p className="font-medium mb-2">Preview (first 5 deals):</p>
                  <div className="space-y-2 text-sm">
                    {validationResult.valid.slice(0, 5).map((deal, idx) => (
                      <div key={idx} className="flex justify-between py-1 border-b last:border-0">
                        <span className="font-medium">{deal.data.athlete_name}</span>
                        <span className="text-muted-foreground">{deal.data.brand_name}</span>
                        <span className="text-muted-foreground">${deal.data.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Progress */}
          {isProcessing && progress > 0 && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-center text-muted-foreground">
                Importing deals... {Math.round(progress)}%
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={
                !validationResult ||
                validationResult.valid.length === 0 ||
                isProcessing
              }
            >
              Import {validationResult?.summary.valid || 0} Deals
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
